// Staff accounts (the Staff tab of Users): invite, change role, disable, sign-in help, expert licence details.
import { api } from "../lib/api";
import { fmt, t, tl } from "../lib/i18n";
import { session } from "../lib/session";
import { h, invalidate, resource } from "../lib/ui";
import { act, Async, Button, Chip, Dialog, Field, formData, isBusy, openDialog, Select } from "../shared/components";
import { DeliveryChoice, showFirstSignIn, SignInSection } from "./users";

function roleOptions(roles: any[]) {
  return roles.map((r) => ({ value: r.key, label: tl(r.name) }));
}

function inviteDialog(roles: any[]) {
  openDialog((close) => (
    <Dialog title={t("admin.staff.inviteTitle")} onClose={close}>
      <form class="stack" onSubmit={(e: Event) => {
        const d = formData(e);
        act("staff.invite", async () => {
          const res = await api.post("/api/admin/staff", {
            name: d.name, email: d.email, phone: d.phone, role: d.role, locale: d.locale,
            delivery: d.delivery ?? "link", ...(d.delivery === "temp_password" && d.tempPassword ? { tempPassword: d.tempPassword } : {}),
          });
          close();
          invalidate("admin.staff");
          showFirstSignIn(res, d.name, d.email, d.delivery === "temp_password" ? d.tempPassword : null);
        });
      }}>
        <div class="cols-2">
          <Field label={t("fields.name")} id="s-name"><input class="input" id="s-name" name="name" required maxLength={120} /></Field>
          <Field label={t("fields.email")} id="s-email"><input class="input" id="s-email" name="email" type="email" required /></Field>
        </div>
        <div class="cols-2">
          <Field label={t("fields.phone")} id="s-phone"><input class="input" id="s-phone" name="phone" type="tel" /></Field>
          <Field label={t("fields.role")} id="s-role"><Select id="s-role" name="role" value="expert" options={roleOptions(roles)} /></Field>
        </div>
        <Field label={t("fields.locale")} id="s-locale">
          <Select id="s-locale" name="locale" value="en" options={[{ value: "en", label: t("languages.en") }, { value: "fr", label: t("languages.fr") }]} />
        </Field>
        <DeliveryChoice id="staff" />
        <div class="row end">
          <Button kind="secondary" onClick={close}>{t("common.cancel")}</Button>
          <Button type="submit" busy={isBusy("staff.invite")}>{t("admin.staff.invite")}</Button>
        </div>
      </form>
    </Dialog>
  ));
}

function editDialog(id: string, roles: any[]) {
  openDialog((close) => {
    const u = (resource("admin.staff", () => api.get("/api/admin/staff")).data ?? []).find((x: any) => x.id === id);
    if (!u) return null;
    const isExpert = roles.find((r) => r.key === u.staff_role)?.permissions?.includes("field.visits");
    return (
    <Dialog title={u.name} onClose={close}>
      <form class="stack" onSubmit={(e: Event) => {
        const d = formData(e);
        const body: any = { name: d.name, phone: d.phone, role: d.role, status: d.status };
        if (isExpert) Object.assign(body, { security_licence_no: d.licence, security_licence_expiry: d.expiry, capacity_hours: d.capacity ? Number(d.capacity) : null });
        act("staff.save", async () => {
          await api.patch(`/api/admin/staff/${u.id}`, body);
          close();
          invalidate("admin.staff");
        }, t("common.saved"));
      }}>
        <div class="cols-2">
          <Field label={t("fields.name")} id="e-name"><input class="input" id="e-name" name="name" value={u.name} required /></Field>
          <Field label={t("fields.phone")} id="e-phone"><input class="input" id="e-phone" name="phone" type="tel" value={u.phone ?? ""} /></Field>
        </div>
        <div class="cols-2">
          <Field label={t("fields.role")} id="e-role" hint={u.id === session.me?.id ? t("admin.staff.ownRole") : undefined}><Select id="e-role" name="role" value={u.staff_role} options={roleOptions(roles)} disabled={u.id === session.me?.id} /></Field>
          <Field label={t("fields.status")} id="e-status">
            <Select id="e-status" name="status" value={u.status === "invited" ? "invited" : u.status}
              options={[
                ...(u.status === "invited" ? [{ value: "invited", label: t("userStatus.invited") }] : [{ value: "active", label: t("userStatus.active") }]),
                { value: "disabled", label: t("userStatus.disabled") },
              ]} />
          </Field>
        </div>
        {isExpert ? (
          <div class="stack">
            <div class="cols-2">
              <Field label={t("admin.staff.licence")} id="e-lic" hint={t("admin.staff.licenceHint")}><input class="input" id="e-lic" name="licence" value={u.security_licence_no ?? ""} /></Field>
              <Field label={t("admin.staff.licenceExpiry")} id="e-exp"><input class="input" id="e-exp" name="expiry" type="date" value={u.security_licence_expiry ?? ""} /></Field>
            </div>
            <Field label={t("admin.staff.capacity")} id="e-cap" hint={t("admin.staff.capacityHint")}><input class="input" id="e-cap" name="capacity" inputMode="numeric" value={u.capacity_hours ?? ""} /></Field>
          </div>
        ) : null}
        <SignInSection u={u} kind="staff" reload={() => invalidate("admin.staff")} />
        <div class="row end">
          <Button kind="secondary" onClick={close}>{t("common.cancel")}</Button>
          <Button type="submit" busy={isBusy("staff.save")}>{t("common.save")}</Button>
        </div>
      </form>
    </Dialog>
    );
  });
}

export function inviteStaffButton() {
  const roles = resource("admin.roles", () => api.get("/api/admin/roles"));
  return <Button icon="plus" disabled={!roles.data} onClick={() => inviteDialog(roles.data.roles)}>{t("admin.staff.invite")}</Button>;
}

export function StaffTab() {
  const staff = resource("admin.staff", () => api.get("/api/admin/staff"));
  const roles = resource("admin.roles", () => api.get("/api/admin/roles"));
  const roleName = (k: string) => tl(roles.data?.roles.find((r: any) => r.key === k)?.name) || k;
  const soon = (d: string | null) => d && new Date(d).getTime() - Date.now() < 60 * 86400 * 1000;
  return (
    <div class="stack loose">
      <section class="panel">
        {Async(staff, (rows: any[]) => (
          <div class="table-wrap">
            <table class="table">
              <thead><tr><th>{t("fields.name")}</th><th>{t("fields.role")}</th><th>{t("fields.status")}</th><th>{t("admin.staff.homes")}</th><th>{t("admin.staff.licenceExpiry")}</th><th>{t("admin.staff.lastLogin")}</th></tr></thead>
              <tbody>
                {rows.map((u) => (
                  <tr class="link" onClick={() => roles.data && editDialog(u.id, roles.data.roles)}>
                    <td><span class="strong">{u.name}</span>{u.id === session.me?.id ? <span class="xs muted"> ({t("common.you")})</span> : null}<span class="sub">{u.email}</span></td>
                    <td>{roleName(u.staff_role)}</td>
                    <td>
                      <div class="row" style="gap:6px;flex-wrap:wrap">
                        <Chip kind={u.status === "active" ? "good" : u.status === "invited" ? "accent" : "neutral"}>{t(`userStatus.${u.status}`)}</Chip>
                        {u.must_change_password ? <Chip kind="attention">{t("admin.users.tempChip")}</Chip> : null}
                      </div>
                    </td>
                    <td class="tabular">{u.primary_homes || u.backup_homes ? t("admin.staff.homesCount", { p: u.primary_homes, b: u.backup_homes }) : "—"}</td>
                    <td>{u.security_licence_expiry ? <span class={soon(u.security_licence_expiry) ? "strong" : ""} style={soon(u.security_licence_expiry) ? "color:var(--c-critical)" : ""}>{fmt.date(u.security_licence_expiry)}</span> : "—"}</td>
                    <td class="small muted">{u.last_login_at ? fmt.relative(u.last_login_at) : t("admin.staff.never")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </section>
    </div>
  );
}
